/**
 * Every call the app makes, in one place and typed from the contract.
 */
import { request, requestWithVersion, type Envelope } from '@/api/client'
import type {
  AttachmentCreateRequest,
  AttachmentDownloadResponse,
  AttachmentResponse,
  MeResponse,
  SessionResponse,
  Paginated,
  PatientResponse,
  PatientWriteRequest,
  SearchHitResponse,
  UploadTicketResponse,
  VisitPage,
  VisitResponse,
  VisitWriteRequest,
} from '@/api/types'

export const auth = {
  signIn: (body: { email: string; password: string }) =>
    request<SessionResponse>('/v1/auth/session', { method: 'POST', body }),
  signOut: () => request<void>('/v1/auth/session', { method: 'DELETE' }),
  me: (signal?: AbortSignal) => request<MeResponse>('/v1/auth/me', signal ? { signal } : {}),
}

export const patients = {
  search: (q: string, limit: number, cursor: string | null, signal?: AbortSignal) =>
    request<Paginated<SearchHitResponse>>('/v1/patients', {
      query: { q, limit, cursor },
      ...(signal ? { signal } : {}),
    }),
  recent: (limit: number, cursor?: string | null, signal?: AbortSignal) =>
    request<Paginated<SearchHitResponse>>('/v1/patients/recent', {
      query: { limit, cursor },
      ...(signal ? { signal } : {}),
    }),
  get: (id: string, signal?: AbortSignal) =>
    request<PatientResponse>(`/v1/patients/${id}`, signal ? { signal } : {}),
  register: (body: PatientWriteRequest) =>
    request<PatientResponse>('/v1/patients', { method: 'POST', body }),
  update: (id: string, body: PatientWriteRequest) =>
    request<PatientResponse>(`/v1/patients/${id}`, { method: 'PATCH', body }),
  duplicateCheck: (id: string, phone: string) =>
    request<Paginated<SearchHitResponse>>(`/v1/patients/${id}/duplicate-check`, { query: { phone } }),
}

export const visits = {
  /** The first page carries `counts` for the whole record — what the tabs are
   *  labelled from, since the history itself arrives a page at a time. */
  list: (patientId: string, limit: number, cursor: string | null, signal?: AbortSignal) =>
    request<VisitPage>(`/v1/patients/${patientId}/visits`, {
      query: { limit, cursor },
      ...(signal ? { signal } : {}),
    }),
  get: (id: string, signal?: AbortSignal): Promise<Envelope<VisitResponse>> =>
    requestWithVersion<VisitResponse>(`/v1/visits/${id}`, signal ? { signal } : {}),
  create: (patientId: string, body: VisitWriteRequest, idempotencyKey: string) =>
    requestWithVersion<VisitResponse>(`/v1/patients/${patientId}/visits`, {
      method: 'POST',
      body,
      idempotencyKey,
    }),
  update: (id: string, body: VisitWriteRequest, version: number) =>
    requestWithVersion<VisitResponse>(`/v1/visits/${id}`, {
      method: 'PATCH',
      body,
      ifMatch: version,
    }),
  void: (id: string, reason: string | null, version: number) =>
    requestWithVersion<VisitResponse>(`/v1/visits/${id}/void`, {
      method: 'POST',
      body: { reason },
      ifMatch: version,
    }),
}

export const attachments = {
  createTicket: (visitId: string, body: AttachmentCreateRequest) =>
    request<UploadTicketResponse>(`/v1/visits/${visitId}/attachments`, { method: 'POST', body }),
  complete: (attachmentId: string) =>
    request<AttachmentResponse>(`/v1/attachments/${attachmentId}/complete`, { method: 'POST' }),
  /** `inline` is for showing the file; the default saves a copy. */
  download: (attachmentId: string, disposition: 'inline' | 'attachment' = 'attachment') =>
    request<AttachmentDownloadResponse>(`/v1/attachments/${attachmentId}`, {
      query: { disposition },
    }),
  thumbnail: (attachmentId: string) =>
    request<{ thumbnail_url: string | null; expires_in_seconds: number | null }>(
      `/v1/attachments/${attachmentId}/thumbnail`,
    ),
  remove: (attachmentId: string) =>
    request<void>(`/v1/attachments/${attachmentId}`, { method: 'DELETE' }),
}

/** Straight to object storage, so deliberately not through `request`: no
 *  session cookie, no API base URL, and a multipart body. */
export async function uploadToBucket(
  ticket: UploadTicketResponse,
  file: Blob,
  filename: string,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  const form = new FormData()
  for (const [key, value] of Object.entries(ticket.fields)) form.append(key, value)
  form.append('file', file, filename)

  await new Promise<void>((resolve, reject) => {
    // XHR rather than fetch: upload progress is the point. A doctor watching a
    // 12 MB X-ray go up needs to see that it is moving.
    const xhr = new XMLHttpRequest()
    xhr.open('POST', ticket.upload_url)
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total)
    })
    xhr.addEventListener('load', () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed with status ${xhr.status}`)),
    )
    xhr.addEventListener('error', () => reject(new Error('Upload failed')))
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')))
    xhr.send(form)
  })
}
